import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { RenderedHtml } from "modules/editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "components/ui/select";

interface Props {
  pairs: { key: string; value?: string }[];
  options?: string[];
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  disabled?: boolean;
}

export function MatchingPlayer({
  pairs,
  options,
  value,
  onChange,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const id = useId();
  // Candidate payloads contain an independent pool; author previews can derive it.
  const pool = Array.from(
    new Set([
      ...(options ?? []),
      ...pairs.map((pair) => pair.value || "").filter(Boolean),
    ]),
  ).sort();

  return (
    <div className="space-y-3.5">
      {pairs.map((pair, index) => {
        const selectedValue = value[pair.key] || "";
        return (
          <div
            key={pair.key || index}
            className="grid gap-2.5 sm:grid-cols-2 sm:items-center rounded-lg border border-border/60 bg-card/50 p-3 sm:p-3.5"
          >
            <label htmlFor={`${id}-${index}`} className="text-sm font-medium leading-relaxed">
              <RenderedHtml html={pair.key} inline />
            </label>
            <div className="w-full min-w-0">
              <Select
                disabled={disabled}
                value={selectedValue || undefined}
                onValueChange={(next) =>
                  onChange({ ...value, [pair.key]: next })
                }
              >
                <SelectTrigger
                  id={`${id}-${index}`}
                  className="min-h-11 h-auto py-2 w-full bg-background text-sm text-left flex items-center justify-between"
                >
                  {selectedValue ? (
                    <div className="truncate font-normal">
                      <RenderedHtml html={selectedValue} inline />
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-sm font-normal">
                      {t("reading_select_answer")}
                    </span>
                  )}
                </SelectTrigger>
                <SelectContent className="max-h-72 max-w-[90vw] sm:max-w-md">
                  {pool.map((option, optIdx) => (
                    <SelectItem key={optIdx} value={option} className="py-2">
                      <div className="max-w-full overflow-hidden text-sm">
                        <RenderedHtml html={option} inline />
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <select
                aria-hidden="true"
                tabIndex={-1}
                className="sr-only"
                disabled={disabled}
                value={selectedValue}
                onChange={(e) =>
                  onChange({ ...value, [pair.key]: e.target.value })
                }
              >
                <option value="">{t("reading_select_answer")}</option>
                {pool.map((opt, optIdx) => (
                  <option key={optIdx} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      })}
    </div>
  );
}
